import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AppConstants } from '../../app.constants';
import { BotmakerService } from '../../core/services/botmaker.service';
import { OrgSelectorService } from '../../core/services/org-selector.service';
import { TokenService } from '../../core/services/token/token.service';
import { NotificationsService } from '../../sanus/notifications/notifications.service';
import { SnackBarService } from '../../shared/components/snack-bar/snack-bar.service';
import { EmailResponse } from '../../shared/models/email-response.model';
import { MessageResponse } from '../../shared/models/message-response.model';
import { RouteUtils } from '../../shared/utils/route/route.util';

import { AuthenticationLoginDto } from './dtos/authentication-login';
import { AuthenticationLoginResponseDto } from './dtos/authentication-login-response';
import { ChangePasswordDto } from './dtos/change-password';
import { ForgotPasswordDto } from './dtos/forgot-password';
import { WelcomeTutorialDto } from './dtos/welcome-tutorial';

@Injectable({
	providedIn: 'root',
})
export class AuthenticationsService {
	timer: any;

	private readonly endpoint: string = `${environment.apiUrl}/authentication`;

	private session: AuthenticationLoginResponseDto | null = null;

	constructor(
		private readonly botmaker: BotmakerService,
		private readonly http: HttpClient,
		private readonly notificationsService: NotificationsService,
		private readonly organizationSelector: OrgSelectorService,
		private readonly router: Router,
		private readonly snackbarService: SnackBarService,
		private readonly tokenService: TokenService,
	) {}

	changePassword({
		dto,
	}: {
		dto: ChangePasswordDto;
	}): Observable<MessageResponse> {
		const body = {
			password: dto.password,
		};

		return this.http.put<MessageResponse>(
			`${this.endpoint}/v1/change-password`,
			body,
			{ headers: { Authorization: dto.token } },
		);
	}

	closeWelcomeTutorial({
		dto,
	}: {
		dto: WelcomeTutorialDto;
	}): Observable<MessageResponse> {
		return this.http.put<MessageResponse>(`${this.endpoint}/v1/tutorial`, dto);
	}

	forgotPassword({
		dto,
	}: {
		dto: ForgotPasswordDto;
	}): Observable<EmailResponse> {
		return this.http.post<EmailResponse>(
			`${this.endpoint}/v1/forgot-password`,
			dto,
		);
	}

	getSession(): AuthenticationLoginResponseDto | null {
		return this.session;
	}

	login({
		dto,
	}: {
		dto: AuthenticationLoginDto;
	}): Observable<AuthenticationLoginResponseDto> {
		return this.http.post<AuthenticationLoginResponseDto>(
			`${this.endpoint}/v1/login`,
			dto,
		);
	}

	logout(): void {
		this.http.post<MessageResponse>(`${this.endpoint}/v1/logout`, {}).subscribe(
			() => {
				this.organizationSelector.setOrganizationId('');
				this.organizationSelector.setUserOrganization('');
				localStorage.clear();
				sessionStorage.clear();
				this.botmaker.stop();
				this.notificationsService.stopPopups();
				this.router.navigateByUrl(RouteUtils.paths.auth.login);
			},
			(error) => {
				this.snackbarService.showSnack(error.error?.message, 'error');
			},
		);
	}

	refreshToken(): Observable<AuthenticationLoginResponseDto> {
		const token = this.tokenService.getToken();

		return this.http.put<AuthenticationLoginResponseDto>(
			`${this.endpoint}/v1/refresh-token`,
			{ refresh_token: token },
		);
	}

	resetToken(): void {
		this.tokenService.removeToken();
		this.tokenService.removeSession();
	}

	setSession(authResponse: AuthenticationLoginResponseDto): void {
		this.session = authResponse;
	}

	setTimer(token: string): void {
		const jwtToken = JSON.parse(atob(token.split('.')[1]));
		const timestampExpiration = new Date(
			jwtToken.exp * AppConstants.time.miliseconds,
		).getTime();
		const timeBeforeExpiredTokenToRefreshToken =
			AppConstants.jwt.intervalToRefreshToken * AppConstants.time.miliseconds;
		const expireTimeFactor = 2;
		let timeoutToRefresh = timestampExpiration - Date.now();

		// O tempo de atualizar o token antes dele expirar será determinado caso o valor
		// definido no front seja maior que o do back, dividirá o valor pela metade
		// caso contrário, usará o próprio valor definido em timeBeforeExpiredTokenToRefreshToken
		timeoutToRefresh =
			timeoutToRefresh > timeBeforeExpiredTokenToRefreshToken
				? timeoutToRefresh - timeBeforeExpiredTokenToRefreshToken
				: Math.ceil(timeoutToRefresh / expireTimeFactor);
		this.timer = setTimeout(() => {
			this.refreshToken();
		}, timeoutToRefresh);
	}

	setToken(
		token: string,
		refresh: string,
		session: AuthenticationLoginResponseDto,
		keep?: boolean,
	): void {
		if (keep) {
			this.tokenService.saveToken(refresh);
		} else {
			this.tokenService.saveSession(refresh);
		}

		this.tokenService.saveAccessToken(token);

		this.setTimer(token);

		this.setSession(session);
	}
}
